// src/utils/langPath.js
//
// Language in the URL for the public sites (company websites and shops).
//
//   default language      /about            /services/plumbing
//   any other language    /ar/about         /ar/services/plumbing
//
// A link therefore carries its language: an Arabic link opens in Arabic for
// whoever receives it. The prefix is handled by the router itself — the public
// apps render <BrowserRouter basename="/ar"> (LangRouter in
// pages/Storefront/StorefrontI18nContext.js), so every <Link>, navigate() and
// <Navigate> already writes and reads the prefix. What the router cannot reach
// are plain `<a href="/contact">` anchors written by the page builder and
// absolute URLs built from window.location.origin; those go through
// localizeHref()/absoluteUrl() below.
//
// A language segment is two or three letters, optionally with a region
// ("ar", "fr", "pt-br"). A few real top-level routes are also two letters long
// and must never be read as a language ("lp" = product landing page).

const LANG_SEGMENT = /^[a-z]{2,3}(?:-[a-z]{2,4})?$/i;
const RESERVED_SEGMENTS = new Set(["lp"]);

// The prefix currently in effect ("" or "/ar"). Kept in a module variable (set
// by StorefrontI18nProvider on every render) so non-React helpers and plain
// anchors can read it without a context lookup.
let activePrefix = "";

export function setActiveLangPrefix(prefix) {
  activePrefix = prefix || "";
}

export function getActiveLangPrefix() {
  return activePrefix;
}

// isLangSegment — true when `seg` could be a language segment.
export function isLangSegment(seg) {
  return typeof seg === "string" && LANG_SEGMENT.test(seg) && !RESERVED_SEGMENTS.has(seg.toLowerCase());
}

// langFromPath("/ar/about") → "ar"; langFromPath("/about") → "".
export function langFromPath(pathname) {
  const m = /^\/([^/?#]+)/.exec(pathname || "");
  return m && isLangSegment(m[1]) ? m[1].toLowerCase() : "";
}

// urlSegmentForLang — the URL segment a language code is addressed by, or ""
// when the code cannot be written in a URL (then that language is simply not
// addressable and falls back to the stored-choice behaviour).
export function urlSegmentForLang(code) {
  const c = String(code || "").toLowerCase();
  return isLangSegment(c) ? c : "";
}

// stripLangPrefix("/ar/about", "ar") → "/about"; "/ar" → "/".
export function stripLangPrefix(pathname, lang) {
  const p = pathname || "/";
  if (!lang) return p;
  const re = new RegExp(`^/${lang.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=/|$)`, "i");
  return p.replace(re, "") || "/";
}

// addLangPrefix("/about", "ar") → "/ar/about"; "/" → "/ar"; no lang → unchanged.
export function addLangPrefix(path, lang) {
  if (!lang) return path || "/";
  const p = path || "/";
  return `/${lang}${p === "/" ? "" : p}`;
}

// localizeHref — prefix an in-site absolute path ("/contact") with the active
// language; anything else (http:, tel:, mailto:, #anchor, "//cdn…", relative
// paths) and paths that already carry the prefix are returned untouched.
export function localizeHref(href) {
  if (typeof href !== "string" || !activePrefix) return href;
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  if (href === activePrefix || /^\/[^/?#]*/.exec(href)?.[0].toLowerCase() === activePrefix) return href;
  return href === "/" ? activePrefix : activePrefix + href;
}

// absoluteUrl — origin + the active language prefix + an unprefixed path;
// used for canonical / og:url / JSON-LD urls so each language page names itself.
export function absoluteUrl(path = "/") {
  const p = path || "/";
  return `${window.location.origin}${p === "/" ? activePrefix || "/" : activePrefix + p}`;
}
